import { useEffect, useMemo, useRef, useState } from "react";
import { LoaderCircle, RefreshCw, ShieldCheck } from "lucide-react";
import type { WorkspaceFile } from "../types";
import {
  buildPreviewHostUrl,
  isPreviewHostMessage,
  PREVIEW_CHANNEL,
  previewFiles,
  removeLegacyPreviewWorker,
  type PreviewRenderMessage,
} from "../lib/preview";

export function HtmlPreview({ file, files, version }: { file: WorkspaceFile; files: WorkspaceFile[]; version: string }) {
  const iframe = useRef<HTMLIFrameElement>(null);
  const renderSent = useRef(false);
  const [hostEnabled, setHostEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [entryPath, setEntryPath] = useState(file.path);
  const [entryHash, setEntryHash] = useState("");

  useEffect(() => {
    setEntryPath(file.path);
    setEntryHash("");
  }, [file.path, file.projectId, file.chatId]);

  useEffect(() => {
    let active = true;
    void removeLegacyPreviewWorker().finally(() => { if (active) setHostEnabled(true); });
    return () => { active = false; };
  }, []);

  const source = useMemo(
    () => buildPreviewHostUrl(`${file.projectId}-${file.chatId}-${version}-${entryPath}-${refresh}`),
    [entryPath, file.projectId, file.chatId, refresh, version],
  );

  useEffect(() => {
    renderSent.current = false;
    setLoaded(false);
    setError(null);
  }, [source]);

  useEffect(() => {
    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.source !== iframe.current?.contentWindow || !isPreviewHostMessage(event.data)) return;
      const message = event.data;
      if (message.type === "ready") {
        if (renderSent.current) return;
        renderSent.current = true;
        const renderMessage: PreviewRenderMessage = {
          channel: PREVIEW_CHANNEL,
          type: "render",
          entryPath,
          entryHash,
          files: previewFiles(files),
        };
        iframe.current.contentWindow?.postMessage(renderMessage, "*");
      } else if (message.type === "rendered" && message.path === entryPath) {
        setLoaded(true);
      } else if (message.type === "error") {
        setError(message.message || "La prévisualisation a échoué.");
      } else if (message.type === "navigate") {
        const target = files.find((candidate) => candidate.path === message.path && candidate.kind === "text" && /\.html?$/i.test(candidate.path));
        if (!target) {
          setError(`Page locale introuvable : ${message.path}`);
          return;
        }
        setEntryPath(target.path);
        setEntryHash(message.hash ?? "");
        if (target.path === entryPath) setRefresh((value) => value + 1);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [entryHash, entryPath, files, source]);

  useEffect(() => {
    if (!hostEnabled || loaded || error) return;
    const timer = window.setTimeout(() => setError("L’hôte de prévisualisation ne répond pas."), 12_000);
    return () => window.clearTimeout(timer);
  }, [error, hostEnabled, loaded, source]);

  return (
    <div className="html-preview">
      <div className="preview-toolbar">
        <span><ShieldCheck size={13} /> Aperçu isolé</span>
        <span className="preview-path">{entryPath}</span>
        <button className="icon-button compact" onClick={() => setRefresh((value) => value + 1)} title="Actualiser l’aperçu"><RefreshCw size={14} /></button>
      </div>
      <div className="preview-frame-wrap">
        {!loaded && !error && <div className="preview-loading"><LoaderCircle size={18} className="spin" /> Chargement de l’aperçu…</div>}
        {error && <div className="preview-error">{error}</div>}
        {hostEnabled && <iframe ref={iframe} key={source} src={source} title={`Aperçu de ${entryPath}`} sandbox="allow-scripts" referrerPolicy="no-referrer" />}
      </div>
    </div>
  );
}
